import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormikContext } from 'formik';
import { flatMap } from 'lodash';
import { FC } from 'react';
import mergeRefs from 'react-merge-refs';
import { Card } from 'src/components/data-display/card';
import {
  CheckboxGroup,
  GroupedFormOption,
} from 'src/components/form/checkbox-group';
import { ErrorMessage } from 'src/components/form/error-message';
import { FieldLayout } from 'src/components/form/field-layout';
import { FormFieldProps } from 'src/components/form/form-field-props';
import { FormOption } from 'src/components/form/form-option';
import { AddonRight, InputContainer } from 'src/components/form/inner-addon';
import { SimpleInput } from 'src/components/form/input';
import { FieldLabel } from 'src/components/form/label';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Stack } from 'src/components/layout/stack';
import { Hint } from 'src/components/text/hint';
import { useDropdown } from 'src/hooks/use-dropdown';
import { useId } from 'src/hooks/use-id';
import { flyoutStyles } from 'src/styles';
import styled from 'styled-components';

const DropdownContent = styled.div<{ showError?: boolean }>`
  ${flyoutStyles};
  max-height: 30.5rem;
  overflow: auto;
  z-index: 1;
`;

type Props = {
  hideLabel?: boolean;
  stacked?: boolean;
  options: FormOption<string>[] | GroupedFormOption<string>[];
  /**
   * @deprecated Use this pattern to create custom error messages.
   *
   * ```
   * import { number } from 'yup';
   *
   * const validation = number
   *   .test(
   *     'No17',
   *     () => (label: string) => `${label} can't be 17.`,
   *     (value, context) => value !== 17
   *   );
   * ```
   */
  getCustomErrorMessage?: (message: string) => string | undefined;
  placeholder?: string;
  disabled?: boolean;
} & FormFieldProps;

export function getPrettyValue(
  options: FormOption<string>[] | GroupedFormOption<string>[],
  value: string[]
): string {
  return (
    isGroupedFormOption(options) ? flatMap(options, (o) => o.options) : options
  )
    .filter((option) => value.find((item) => item === option.value))
    .map((option) => option.label)
    .join(', ');
}

function isGroupedFormOption(
  options: FormOption<string>[] | GroupedFormOption<string>[]
): options is GroupedFormOption<string>[] {
  if (options.length === 0) {
    return false;
  }
  return !!(options[0] as GroupedFormOption<string>).group;
}

export const CheckboxGroupFilter: FC<Props> = ({
  hideLabel = false,
  label,
  name,
  options,
  hint,
  info,
  stacked = true,
  getCustomErrorMessage,
  placeholder,
  disabled,
  markAsRequired,
}) => {
  const { active, setActive, setWrapperElement, ...dropdown } = useDropdown({
    placement: 'bottom-start',
  });
  const { getFieldProps } = useFormikContext<any>();
  const focusRef = useFocusManager();
  const [, showError, error] = useCustomField<string[]>(name, label);

  const fieldId = useId();
  const { value } = getFieldProps<string[]>(name);
  const prettyValue = getPrettyValue(options, value);
  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;
  const ariaLabel = hideLabel ? displayLabel : undefined;

  const checkboxInput = (
    <InputContainer>
      <SimpleInput
        id={fieldId}
        ref={mergeRefs([focusRef, dropdown.refs.setReference])}
        value={prettyValue}
        readOnly
        error={showError}
        onFocus={() => setActive(true)}
        aria-label={ariaLabel}
        data-testid={name}
        placeholder={placeholder}
        disabled={disabled}
      />
      <AddonRight showError={showError} disabled={disabled}>
        <FontAwesomeIcon
          icon={active ? faCaretUp : faCaretDown}
          aria-label={active ? 'Filter is open.' : 'Filter is closed.'}
        />
      </AddonRight>
    </InputContainer>
  );

  return (
    <div ref={setWrapperElement}>
      {!hideLabel ? (
        <FieldLayout stacked={stacked}>
          <FieldLabel
            name={name}
            displayLabel={displayLabel}
            fieldId={fieldId}
            info={info}
            showError={showError}
          />

          <Stack>
            {checkboxInput}
            {hint && <Hint mode="formInput" children={hint} />}
          </Stack>
        </FieldLayout>
      ) : (
        checkboxInput
      )}

      {active && (
        <DropdownContent
          ref={dropdown.refs.setFloating}
          style={dropdown.style}
          showError={showError}
          data-testid={`${name}DropdownContent`}
        >
          <Card>
            <CheckboxGroup
              {...{ label, name, options }}
              hideLabel
              stackedItems
              toggleAll
              isGroupFilter={true}
            />
          </Card>
        </DropdownContent>
      )}
      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          getCustomErrorMessage={getCustomErrorMessage}
          error={error}
          label={label}
          type="selectable"
        />
      )}
    </div>
  );
};
